import { useCallback, useRef, useState } from 'react';
import { ApiError, type AxisSweepSubjectType, runAxisAffinityBackfillBatch } from './api';
import {
  type AxisSweepRunState,
  accumulateBatch,
  initialAxisSweepRunState,
  shouldContinueSweep,
} from './axisSweepState';

/**
 * ADMIN-SWEEP (2026-07-06) — the hook behind the "Corpus tagging sweep"
 * admin card (Settings.tsx). One independent AxisSweepRunState PER
 * subjectType (achievement/facet) — mirrors useSettings.ts's own per-action
 * phase-machine discipline: running the achievements sweep must never block
 * or get confused with the facets sweep's own progress/error state.
 *
 * The loop itself: call POST /api/admin/axis-affinities/backfill for the
 * given subjectType, fold the served batch into the running totals
 * (axisSweepState.ts's accumulateBatch — real served numbers only, never
 * estimated ahead of the response), and keep firing another batch call
 * while shouldContinueSweep(batch) is true. `running` tracks in-flight
 * state per subjectType so the card can disable both buttons independently
 * while EITHER sweep is active (never run two batches for the same
 * subjectType concurrently — a second click while running is a no-op).
 *
 * NO polling, no background timer: this is a synchronous await-loop driven
 * entirely by the server's own isLastBatch signal, never a client-side
 * guess at how many batches remain.
 */

type SubjectTypeState = Record<AxisSweepSubjectType, AxisSweepRunState>;

function initialSubjectTypeState(): SubjectTypeState {
  return {
    achievement: initialAxisSweepRunState(),
    facet: initialAxisSweepRunState(),
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export function useAxisSweep() {
  const [state, setState] = useState<SubjectTypeState>(initialSubjectTypeState);
  // Guards against a double-tap starting a SECOND concurrent loop for the
  // same subjectType while one is already running — refs (not state) so the
  // check is synchronous at call time, no stale-closure race.
  const runningRef = useRef<Record<AxisSweepSubjectType, boolean>>({
    achievement: false,
    facet: false,
  });

  const runSweep = useCallback(async (subjectType: AxisSweepSubjectType) => {
    if (runningRef.current[subjectType]) return;
    runningRef.current[subjectType] = true;

    setState((s) => ({
      ...s,
      [subjectType]: { phase: 'running', totals: initialAxisSweepRunState().totals, error: null },
    }));

    try {
      let isLastBatch = false;
      while (!isLastBatch) {
        const result = await runAxisAffinityBackfillBatch(subjectType);
        isLastBatch = !shouldContinueSweep(result);
        setState((s) => ({
          ...s,
          [subjectType]: {
            phase: isLastBatch ? 'done' : 'running',
            totals: accumulateBatch(s[subjectType].totals, result),
            error: null,
          },
        }));
      }
    } catch (err) {
      setState((s) => ({
        ...s,
        [subjectType]: { ...s[subjectType], phase: 'error', error: errorMessage(err) },
      }));
    } finally {
      runningRef.current[subjectType] = false;
    }
  }, []);

  /** Resets one subjectType's run state back to idle — e.g. so a "run it
   *  again" tap after a completed or errored sweep starts from a clean
   *  totals slate rather than adding onto the previous run's numbers. */
  const resetSweep = useCallback((subjectType: AxisSweepSubjectType) => {
    if (runningRef.current[subjectType]) return;
    setState((s) => ({ ...s, [subjectType]: initialAxisSweepRunState() }));
  }, []);

  return { state, runSweep, resetSweep };
}
